import { mixToMono, adaptiveThreshold } from "./audioUtils";

/**
 * Onset detection for the Riffed guessing game.
 *
 * Uses energy-derivative approach: computes RMS energy in sliding windows,
 * then finds sharp increases (attacks). More robust than spectral flux
 * for sustained sounds like piano chords and vocal harmonies.
 */

export interface OnsetResult {
  /** Timestamps (in seconds) of detected onsets */
  onsets: number[];
  /** The audio buffer duration in seconds */
  duration: number;
  /** True if algorithm fell back to evenly-spaced onsets */
  fallback: boolean;
}

export interface OnsetDetectorOptions {
  /** Minimum gap between onsets in seconds (default: 0.35) */
  minGapSec?: number;
  /** Energy window size in milliseconds (default: 20) */
  energyWindowMs?: number;
  /** Hop size in milliseconds (default: 5) */
  hopMs?: number;
  /** Threshold multiplier for adaptive threshold (default: 1.6) */
  thresholdMultiplier?: number;
}

export function detectOnsets(
  audioBuffer: AudioBuffer,
  options: OnsetDetectorOptions = {}
): OnsetResult {
  const {
    minGapSec = 0.35,
    energyWindowMs = 20,
    hopMs = 5,
    thresholdMultiplier = 1.6,
  } = options;

  const sampleRate = audioBuffer.sampleRate;
  const mono = mixToMono(audioBuffer);

  // 1. Compute RMS energy in sliding windows
  const winSamples = Math.round((sampleRate * energyWindowMs) / 1000);
  const hopSamples = Math.round((sampleRate * hopMs) / 1000);
  const numFrames = Math.floor((mono.length - winSamples) / hopSamples);
  const energy = new Float32Array(numFrames);

  for (let i = 0; i < numFrames; i++) {
    const off = i * hopSamples;
    let sum = 0;
    for (let j = 0; j < winSamples; j++) sum += mono[off + j] ** 2;
    energy[i] = Math.sqrt(sum / winSamples);
  }

  // 2. Compute the derivative (energy increase only)
  const diff = new Float32Array(numFrames);
  for (let i = 1; i < numFrames; i++) {
    diff[i] = Math.max(0, energy[i] - energy[i - 1]);
  }

  // 3. Adaptive threshold on the derivative
  const threshWin = Math.round(200 / hopMs);
  const thresholds = adaptiveThreshold(diff, thresholdMultiplier, threshWin);

  // 4. Pick peaks above threshold with minimum gap
  const hopDuration = hopMs / 1000;
  const onsets: number[] = [];

  for (let i = 2; i < numFrames - 1; i++) {
    if (
      diff[i] > thresholds[i] &&
      diff[i] > diff[i - 1] &&
      diff[i] >= diff[i + 1]
    ) {
      const t = i * hopDuration;
      if (onsets.length === 0 || t - onsets[onsets.length - 1] >= minGapSec) {
        onsets.push(t);
      }
    }
  }

  // 5. Ensure we always have at least 6 onsets (fallback: evenly spaced)
  if (onsets.length < 6) {
    const spacing = audioBuffer.duration / 8;
    const fallback: number[] = [];
    for (let i = 0; i < 7; i++) fallback.push(spacing * (i + 0.5));
    return { onsets: fallback, duration: audioBuffer.duration, fallback: true };
  }

  return { onsets, duration: audioBuffer.duration, fallback: false };
}

/**
 * Given onset timestamps and a stage number (1-6),
 * returns the end timestamp for that stage's clip.
 * Stage 1 = first note only, Stage 6 = first 6 notes.
 */
export function getClipEndForStage(
  onsets: number[],
  stage: number,
  startSec: number,
  totalDuration: number
): number {
  const relevantOnsets = onsets.filter((t) => t >= startSec);

  if (relevantOnsets.length === 0) {
    return Math.min(startSec + stage, totalDuration);
  }

  if (stage >= relevantOnsets.length) {
    const lastOnset = relevantOnsets[relevantOnsets.length - 1];
    return Math.min(lastOnset + 0.5, totalDuration);
  }

  return relevantOnsets[stage] - 0.02;
}

// --- Internal helpers ---

